import { alpha, Box, useTheme } from '@mui/material';
import { parseSiteOverview } from 'src/graphql/entities/address-summary';
import { ChipCell } from 'src/pages/CustomViews/custom-views-entities-table';
import { Donut } from 'src/pages/Sites/SiteOverview/site-overview.elements';
import {
  humanize,
  riskTone,
  sentimentTone,
} from 'src/pages/Sites/SiteOverview/site-overview.helpers';
import { toneColor } from 'src/pages/Sites/SiteOverview/site-overview.styles';
import useCustomViewsStore from 'src/store/custom-views';

const GRID_DONUT_SIZE = 32;
const GRID_DONUT_SIZE_COMPACT = 26;
const UNKNOWN_LABEL = humanize('unknown');

const gridDonutCellSx = {
  position: 'absolute',
  top: '50%',
  left: 8,
  transform: 'translateY(-50%)',
  lineHeight: 0,
} as const;

type SiteSummaryRow = {
  addressSummary?: {
    intermediateSummary?: string | Record<string, unknown> | null;
  } | null;
};

const UnknownChip = () => {
  const theme = useTheme();
  const color = toneColor(theme, 'neut');
  return (
    <ChipCell label={UNKNOWN_LABEL} textColor={color} backgroundColor={alpha(color, 0.16)} />
  );
};

export const SiteHealthField = ({ row }: { row: SiteSummaryRow }) => {
  const gridDensity = useCustomViewsStore((state) => state.gridDensity);
  const health = parseSiteOverview(row.addressSummary?.intermediateSummary)?.siteHealth;
  if (!health || health.assessmentStatus === 'not_assessed' || health.overallScore === null) {
    return <UnknownChip />;
  }

  return (
    <Box sx={gridDonutCellSx}>
      <Donut
        score={health.overallScore}
        tone={riskTone(health.riskLevel)}
        size={gridDensity === 'compact' ? GRID_DONUT_SIZE_COMPACT : GRID_DONUT_SIZE}
        scoreFontSize={gridDensity === 'compact' ? 11 : undefined}
      />
    </Box>
  );
};

export const SiteSentimentField = ({ row }: { row: SiteSummaryRow }) => {
  const theme = useTheme();
  const sentiment = parseSiteOverview(
    row.addressSummary?.intermediateSummary,
  )?.clientSentiment;
  if (
    !sentiment ||
    sentiment.assessmentStatus === 'unknown' ||
    sentiment.overall === 'unknown'
  ) {
    return <UnknownChip />;
  }

  const tone = sentimentTone(sentiment.overall);
  return (
    <ChipCell
      label={humanize(sentiment.overall)}
      textColor={toneColor(theme, tone)}
      backgroundColor={alpha(toneColor(theme, tone), tone === 'good' ? 0.14 : 0.16)}
    />
  );
};
